/*
  global documentFake, SportsFeatures,
  NetworkConfig, BaseUrl, FontName, SingleSignOnEnvironment, Version
*/
import React from 'react';
import _ from 'lodash';
import axios from 'axios';
import { MORE_LIKE_POSTER, SIMPLIFIED_POSTER } from 'components/PosterV2';
import { SERIES_CLIP_TILE, SERIES_SEASON_TILE } from 'components/TileV2';
import {
  FORMAT_DAY_OF_WEEK,
  FORMAT_MONTH_DAY_YEAR,
  FORMAT_MONTH_SLASH_DAY,
  FORMAT_TIME,
  addDays,
  differenceInDays,
  differenceInMilliseconds,
  differenceInSeconds,
  format,
  getTimestamp,
  isAfter,
  isSameDay,
  msToDate,
  now,
  stringToDate,
} from 'helpers/DateTimeHelper';
import Error404 from 'screens/Error404';
import Error500 from 'screens/Error500';
import { formatDurationInSeconds, formatMetadata } from 'helpers/VideoHelper';
import { setCookie, getCookie, removeCookie, cookieFactory } from 'helpers/Storage';
import Regex from 'helpers/Regex';
import { parseJwt } from 'helpers/AnonymousHelper';
import { MVPD_COOKIE_NAME, MVPD_LOGOUT_COOKIE_NAME } from 'helpers/AuthHelper';
import { getSitePlatform } from 'helpers/BreakpointHelper';
import { NETWORK_LOGO_IMAGE, getDynamicImage } from 'helpers/ImageHelper';
import { helperGetStaticContentUrl } from 'helpers/RouteHelper';
import { getBaseContentUrl, helperGetApiKey } from 'helpers/ApiHelper';
import { isBrowserIE } from 'helpers/GlobalHelper';

export const FNC = 'fnc';
export const FOX = 'fox';
export const NATION = 'foxnation'; //eslint-disable-line

export const LABEL_CLIPS_AND_EXTRAS = 'Clips & Extras';
export const LABEL_CLIPS = 'Clips';

export const TYPE_EPG_LISTING = 'EPGListing';
const TYPE_FULL_EPISODE = 'fullEpisode';
const TYPE_SPORTING_EVENT = 'sportingEvent';

export const PAGE_TYPE_ARTICLE = 'article';
export const PAGE_TYPE_CHARACTERS = 'characters';
export const PAGE_TYPE_CLIPS = 'clips';
export const PAGE_TYPE_CREDITS = 'credits';
export const PAGE_TYPE_PROMO_LIST = 'promo';
export const PAGE_TYPE_WATCH_FREE = 'watch-free';

const PREVIEW_PASS_5_MVPD = 'TempPass_fbcfox_5min';
const PREVIEW_PASS_60_MVPD = 'TempPass_fbcfox_60min';

export const COLLECTION_TYPE_AFFILIATE_CATEGORY_LIST = 'affiliateCategoryList';
export const COLLECTION_TYPE_FTSLOCAL = 'ftsLocal';
export const COLLECTION_TYPE_LOCAL_CLIPS = 'localClips';
export const COLLECTION_TYPE_SHOWCASE = 'showcase';
export const COLLECTION_TYPE_SHOWCASE_CAROUSEL = 'showcaseCarousel';
const COLLECTION_TYPE_SHOWCASE_CAROUSEL_CONTENT = 'showcaseCarouselContent';

export const isCarouselShowcase = collectionType =>
  collectionType === COLLECTION_TYPE_SHOWCASE_CAROUSEL ||
  collectionType === COLLECTION_TYPE_SHOWCASE_CAROUSEL_CONTENT;

export const COLLECTION_TYPE_LOCAL_LIVE = 'live around the country';
export const COLLECTION_TYPE_GENRE = 'genre';
export const COLLECTION_TYPE_LIVE = 'live';
export const NEWS_GENRE = 'News';
export const VIDEO_TYPE = 'clip';

export const TAB_PRIMETIME = _.get(window.Language, 'global_primetimeTab');
export const TAB_SCHEDULE = _.get(window.Language, 'global_scheduleTab');

const NO_AUTHORIZING_NETWORK = 'no authorizing network';
export const FOX_WEATHER_NETWORK = 'foxweather';

export const NAME_RECOMMENDED_SERIES = 'You May Also Like';

export const LINK_REL = 'noopener noreferrer';

export const PANEL_TYPE_PLAYER_BANNER = 'playerBanner';
export const PANEL_TYPE_PROMOS = 'promos';
export const PANEL_TYPE_VIDEOS = 'videos';
export const PANEL_TYPE_SHOWS = 'shows';
export const PANEL_TYPE_PARTNER_SHOWS = 'partnerShowPanel';
export const PANEL_TYPE_PARTNER_VIDEO = 'partnerVideoPanel';

const AD_BLOCK_DETECTED_COOKIE_NAME = 'ad-block-detected';
const NETWORKS_DISPLAY_ORDER_NAME = 'network-display-order';
const MVPD_SUBSCRIPTION_COOKIE_NAME = 'mvpd-subscription';
export const NATION_ENTITLED_COOKIE_NAME = 'nation-entitled';
export const CCPA_VERIFY_COOKIE_NAME = 'ccpa-verified';
export const CCPA_OPT_OUT_COOKIE_NAME = 'usprivacy';
export const OOM_LIVE_PLAYER_URL_COOKIE_NAME = 'oom-live-player-url';
export const OOM_LIVE_PLAYER_IMAGE_COOKIE_NAME = 'oom-live-player-image';
export const LOCAL_CLIP_PAGE_COOKIE_NAME = 'local-clip-page';
export const LOCAL_CLIP_INITIAL_ID = 'local-clip-initial-id';
export const LOCAL_CLIP_INITIAL_PLAYER_URL = 'local-clip-player-url';
const LOCAL_CLIP_PLAYLIST = 'local-clip-playlist';
const LOCAL_CLIP_SECTION_TITLE = 'local-clip-title';
export const LOCAL_CHANNEL_STATION_PICKER = 'local-channel-station-picker';
export const LOCAL_IN_MARKET = 'localInMarket';
export const CCPA_DO_NOT_SELL_NOT_SEEN = '1---';
export const CCPA_DO_NOT_SELL_SEEN = '1YNN';
export const CCPA_DO_NOT_SELL_CHECKED = '1YYN';

export const HTML_ENTITY_BULLET = String.fromCharCode(8226);

const WATCH_ON_FOX_NOW_ARTICLE_ID = '598b6e18311288001fef0f04';

export const v1Override = { 'X-Delegated-Auth-Flow-Off': true };

export const isFoxNowAppPage = articleId => articleId.indexOf(WATCH_ON_FOX_NOW_ARTICLE_ID) !== -1;

export const getNetworkBranding = () => _.get(NetworkConfig, 'branding');

export const getNetworkBrandingName = () => _.get(NetworkConfig, 'name');

export const isFox = () => getNetworkBranding() === FOX;

export const isHomePage = () => window.location.pathname === '/';

export const getNetworksDisplayOrder = () => {
  let networkDisplayOrder = getNetworkDisplayOrder();
  if (!networkDisplayOrder) {
    networkDisplayOrder = _.get(window.ExternalConfig, 'auth.success.networksDisplayOrder', []);
    if (networkDisplayOrder.length > 0) {
      setNetworkDisplayOrder({ value: networkDisplayOrder });
    }
  }
  return networkDisplayOrder;
};

export const getNetworkList = () =>
  _.get(window.ExternalConfig, 'auth.success.networksDisplayOrder', []);

export const getMVPDLogoutCookie = () => getCookie({ name: MVPD_LOGOUT_COOKIE_NAME });

export const getMVPDFromCookie = () => {
  const mvpdCookie = getMVPDCookie();
  const mvpd = _.get(mvpdCookie, 'mvpd', null);
  return mvpd;
};

/* eslint-disable */
export const getPurchaseSourceCookie = () => {
  const purchaseSource = getCookie({ name: 'purchaseSource' });
  return purchaseSource || '';
};
/* eslint-enable */
export const hasEntitlementOverride = () => {
  const entitlementCookieName = _.get(window.ExternalConfig, 'entitlementCookieName');
  const entitlementCookie = getCookie({ name: entitlementCookieName });
  if (entitlementCookie) {
    const isNation = _.get(window.ExternalConfig, 'analytics.app_name', false) === NATION;
    if (isNation) return 'foxnation';
  }
  return false;
};

export const parseJSON = data => {
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

export const getFontClassName = style => _.get(style, `${FontName}Font`);

export const isClip = videoType => videoType === 'clip';
export const isClipTrailer = clipType => clipType === 'Trailer';
export const isEpisode = videoType => videoType === TYPE_FULL_EPISODE;
export const isLive = type => type === TYPE_EPG_LISTING;
export const isMovie = videoType => videoType === 'movie';
export const isMovieSeries = seriesType => seriesType === 'movie';
const isNews = seriesType => seriesType === 'news';
export const isSeries = seriesType => seriesType === 'series';
const isSeriesCollection = seriesType => seriesType === 'seriesCollection';
export const isSpecial = seriesType => seriesType === 'special' || seriesType === 'other';
const isCategoryTypePersonality = categoryType => categoryType === 'personality';
const isTypeCategory = type => type === 'Category';
export const isTypePromo = type => type === 'Promo';
export const isTypeSeries = type => type === 'Series';
export const isTypeEPGListing = type => type === 'EPGListing';
export const isTypeVideo = type => type?.toLowerCase() === 'video';
export const isSports = seriesType => seriesType === 'sportingEvent';

export const DEVICE_ANDROID = 'DEVICE_ANDROID';
export const DEVICE_IOS = 'DEVICE_IOS';

export const sportsFeatures = () => SportsFeatures;
const SPORTS_MAX_DESC_LENGTH = 150;

export const API_BASEURL_OVERRIDE = 'API_BASEURL_OVERRIDE';
export const DEBUG_PANEL_AD_DEBUG = 'DEBUG_PANEL_AD_DEBUG';
export const DEBUG_PANEL_AD_SITE_SECTION = 'DEBUG_PANEL_AD_SITE_SECTION';
export const DEBUG_PANEL_API_BASEURL = 'DEBUG_PANEL_API_BASEURL';
export const DEBUG_PANEL_BLACKOUT_FETCH_INTERVAL = 'DEBUG_PANEL_BLACKOUT_FETCH_INTERVAL';
export const DEBUG_PANEL_CITY = 'DEBUG_PANEL_CITY';
export const DEBUG_PANEL_CITY_LAT = 'DEBUG_PANEL_CITY_LAT';
export const DEBUG_PANEL_CITY_LONG = 'DEBUG_PANEL_CITY_LONG';
export const DEBUG_PANEL_CLIENTLESS_RECURRING_PRE_AUTHZ_REFRESH =
  'DEBUG_PANEL_CLIENTLESS_RECURRING_PRE_AUTHZ_REFRESH';
export const DEBUG_PANEL_CLIENTLESS_TOKEN_EXPIRE_REFRESH =
  'DEBUG_PANEL_CLIENTLESS_TOKEN_EXPIRE_REFRESH';
export const DEBUG_PANEL_DESIGN_VERSION = 'DEBUG_PANEL_DESIGN_VERSION';
export const DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_CURRENT_HOVER =
  'DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_CURRENT_HOVER';
export const DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_SIMPLIFIED_HOVER =
  'DEBUG_PANEL_FLAG_ADDITIONAL_POSTER_SIMPLIFIED_HOVER';
export const DEBUG_PANEL_FLAG_BLACKOUT = 'DEBUG_PANEL_FLAG_BLACKOUT';
export const DEBUG_PANEL_FLAG_BUYTHROUGH_EXTERNAL_SIGNUP_LOGIN =
  'DEBUG_PANEL_FLAG_BUYTHROUGH_EXTERNAL_SIGNUP_LOGIN';
export const DEBUG_PANEL_FLAG_CCPA_OPT_OUT = 'DEBUG_PANEL_FLAG_CCPA_OPT_OUT';
export const DEBUG_PANEL_FLAG_EPGV2 = 'DEBUG_PANEL_FLAG_EPGV2';
export const DEBUG_PANEL_FLAG_EPG_MINI_PLAYER = 'DEBUG_PANEL_FLAG_EPG_MINI_PLAYER';
export const DEBUG_PANEL_FLAG_EPG_SEARCH = 'DEBUG_PANEL_FLAG_EPG_SEARCH';
export const DEBUG_PANEL_FLAG_GLOBAL_PLAYER = 'DEBUG_PANEL_FLAG_GLOBAL_PLAYER';
export const DEBUG_PANEL_FLAG_GLOBAL_PLAYER_AUTOPLAY = 'DEBUG_PANEL_FLAG_GLOBAL_PLAYER_AUTOPLAY';
export const DEBUG_PANEL_FLAG_HERO_CAROUSEL = 'DEBUG_PANEL_FLAG_HERO_CAROUSEL';
export const DEBUG_PANEL_FLAG_MVPD_DEEPLINK = 'DEBUG_PANEL_FLAG_MVPD_DEEPLINK';
export const DEBUG_PANEL_FLAG_PREVIEW_MODE = 'DEBUG_PANEL_FLAG_PREVIEW_MODE';
export const DEBUG_PANEL_FLAG_PREVIEW_PASS = 'DEBUG_PANEL_FLAG_PREVIEW_PASS';
export const DEBUG_PANEL_FLAG_CONSOLIDATED_SIGNIN = 'DEBUG_PANEL_FLAG_CONSOLIDATED_SIGNIN';
export const DEBUG_PANEL_FLAG_PREVIEW_PASS_60_MIN = 'DEBUG_PANEL_FLAG_PREVIEW_PASS_60_MIN';
export const DEBUG_PANEL_FLAG_SECOND_SCREEN_PROFILE = 'DEBUG_PANEL_FLAG_SECOND_SCREEN_PROFILE';
export const DEBUG_PANEL_FLAG_SERIES_ANONYMOUS = 'DEBUG_PANEL_FLAG_SERIES_ANONYMOUS';
export const DEBUG_PANEL_FLAG_SERIES_V2 = 'DEBUG_PANEL_FLAG_SERIES_V2';
export const DEBUG_PANEL_FLAG_SSA_REDIRECT = 'DEBUG_PANEL_FLAG_SSA_REDIRECT';
export const DEBUG_PANEL_FLAG_STRIKE_BANNER = 'DEBUG_PANEL_FLAG_STRIKE_BANNER';
export const DEBUG_PANEL_FLAG_TILE_LIVE_TILE_V2 = 'DEBUG_PANEL_FLAG_TILE_LIVE_TILE_V2';
export const DEBUG_PANEL_FLAG_USE_MARINE_API_KEY = 'DEBUG_PANEL_FLAG_USE_MARINE_API_KEY';
export const DEBUG_PANEL_LOCATION_COOKIE_DURATION = 'DEBUG_PANEL_LOCATION_COOKIE_DURATION';
export const DEBUG_PANEL_TIMEOUT_DISABLED = 'DEBUG_PANEL_TIMEOUT_DISABLED';
export const DEBUG_PANEL_USER = 'DEBUG_PANEL_USER';
export const DEBUG_PANEL_VIDEO_CDN = 'DEBUG_PANEL_VIDEO_CDN';
export const DEBUG_PANEL_VIDEO_ORIGIN = 'DEBUG_PANEL_VIDEO_ORIGIN';
const {
  getValue: getLocalClipPage,
  setValue: setLocalClipPage,
  removeValue: removeLocalClipPage,
} = cookieFactory(LOCAL_CLIP_PAGE_COOKIE_NAME);
const {
  getValue: getOomLivePlayerEnabled,
  setValue: setOomLivePlayerEnabled,
  removeValue: removeOomLivePlayerEnabled,
} = cookieFactory(OOM_LIVE_PLAYER_URL_COOKIE_NAME);
const {
  getValue: getLocalClipSectionTitle,
  setValue: setLocalClipSectionTitle,
  removeValue: removeLocalClipSectionTitle,
} = cookieFactory(LOCAL_CLIP_SECTION_TITLE);
const {
  getValue: getLocalClipPlaylist,
  setValue: setLocalClipPlaylist,
  removeValue: removeLocalClipPlaylist,
} = cookieFactory(LOCAL_CLIP_PLAYLIST);

const { getValue: getAdDebug } = cookieFactory(DEBUG_PANEL_AD_DEBUG);
const { getValue: getAdSiteSection } = cookieFactory(DEBUG_PANEL_AD_SITE_SECTION);
const { getValue: getPrivacyParameterCookie, setValue: setPrivacyParameterCookie } = cookieFactory(
  CCPA_OPT_OUT_COOKIE_NAME,
);
const { getValue: getLatitude, setValue: setLatitude } = cookieFactory('COOKIE_GEO_LOCATION_LAT');
const { getValue: getLongitude, setValue: setLongitude } = cookieFactory(
  'COOKIE_GEO_LOCATION_LONG',
);
const { getValue: getCookieTimestamp, setValue: setCookieTimeStamp } = cookieFactory(
  'COOKIE_GEO_LOCATION_DATE_MS',
);
const { setValue: setCookieModalTimeStamp } = cookieFactory('COOKIE_GEO_LOCATION_MODAL_MS');
const {
  getValue: getMVPDCookie,
  setValue: setMVPDCookie,
  removeValue: removeMVPDCookie,
} = cookieFactory(MVPD_COOKIE_NAME);
const { setValue: setMVPDLogoutCookie } = cookieFactory(MVPD_LOGOUT_COOKIE_NAME);
const { getValue: getNetworkDisplayOrder, setValue: setNetworkDisplayOrder } = cookieFactory(
  NETWORKS_DISPLAY_ORDER_NAME,
);
const { getValue: isMvpdSubscription } = cookieFactory(MVPD_SUBSCRIPTION_COOKIE_NAME);
const {
  getValue: getAdBlockDetectCookie,
  setValue: setAdBlockDetectCookie,
  removeValue: removeAdBlockDetectCookie,
} = cookieFactory(AD_BLOCK_DETECTED_COOKIE_NAME);

export async function createProfile(accessToken = null, profileId = null) {
  let profileConfig = null;
  let profile = null;
  if (!isStaticRender() && !isBrowserIE()) {
    const module = await import('@foxcorp/foxkit-profile-js');
    const baseConfigUrl = getBaseContentUrl();
    const baseUrl =
      baseConfigUrl.substring(baseConfigUrl.length - 1, baseConfigUrl.length) === '/'
        ? baseConfigUrl.substring(0, baseConfigUrl.length - 1)
        : baseConfigUrl;
    const config = {
      appName: 'FOX NOW',
      appVersion: Version,
      apiKey: helperGetApiKey(),
      environment: SingleSignOnEnvironment,
      accessToken, // optional user accessToken
      profileId, // optional user profileId
      baseUrl,
    };
    profileConfig = new module.ProfileConfig(config);
    profile = new module.Profile(profileConfig);
  }
  return { profileConfig, profile };
}

export {
  getAdBlockDetectCookie,
  getAdDebug,
  getAdSiteSection,
  getCookieTimestamp,
  getLatitude,
  getLocalClipPage,
  getLocalClipPlaylist,
  getLocalClipSectionTitle,
  getLongitude,
  getMVPDCookie,
  getOomLivePlayerEnabled,
  getPrivacyParameterCookie,
  isMvpdSubscription,
  removeAdBlockDetectCookie,
  removeMVPDCookie,
  setAdBlockDetectCookie,
  setCookieModalTimeStamp,
  setCookieTimeStamp,
  setLatitude,
  setLocalClipPage,
  setLocalClipPlaylist,
  setLongitude,
  setMVPDCookie,
  setMVPDLogoutCookie,
  setPrivacyParameterCookie,
};

export const isDescTooLong = description => {
  if (!sportsFeatures() || !description) return false;
  if (description.length > SPORTS_MAX_DESC_LENGTH) return true;
  return false;
};

export const getContentTypeName = videoType => {
  if (isMovie(videoType)) return 'Movie';
  return 'Show';
};

export const getVideoTypeName = type => {
  if (isMovie(type)) return 'Movie';
  if (isClip(type)) return 'Clip';
  return 'Episode';
};

export const getDisplayBrand = item => {
  const displayBrand = _.get(item, 'displayBrand');
  if (displayBrand) return displayBrand;
  const network = _.get(item, 'network');

  return network;
};

export const getNetworkLogoUrl = (network, size, logo = window.ExternalConfig.networkLogo) => {
  if (!network || !network.length || !logo) return null;
  const breakpoint = getSitePlatform();
  let networkTemp = network;
  if (networkTemp === 'fbc-fox') networkTemp = FOX;
  const networkComma = network.indexOf(',');
  const networkId = networkComma > 0 ? network.substring(0, networkComma) : networkTemp;
  const url = logo.replace('{network}', networkId);
  return getDynamicImage({
    breakpoint,
    context: NETWORK_LOGO_IMAGE,
    url,
  });
};

export const getConfigNetworkLogoUrl = (network, logo = window.ExternalConfig.networkLogo) => {
  if (!logo) return null;
  return logo.replace('{network}', network);
};

// export const getHeaderLogo = network => {
//   const breakpoint = getSitePlatform();
//   const url = _.get(network, 'images.header.raw', null);

//   if (!url) return null;

//   return getDynamicImage({
//     breakpoint,
//     context: NETWORK_LOGO_IMAGE,
//     url,
//   });
// }; Disabling till CPEWEB-2522 is resolved with consistent images

export const mobileDeviceType = () => {
  const userAgent = _.get(window, 'navigator.userAgent', null);
  if (!userAgent) return null;
  if (userAgent.match(/Android/i)) return DEVICE_ANDROID;
  if (userAgent.match(/iPhone|iPad|iPod/i)) return DEVICE_IOS;
  return null;
};

export const disableSmoothScrollMSIE = () => {
  const userAgent = _.get(window, 'navigator.userAgent', null);
  if (!userAgent) return null;
  if (userAgent.match(/TRIDENT/i)) {
    window.addEventListener('mousewheel', e => {
      e.preventDefault();
      window.scrollTo(0, window.pageYOffset - e.wheelDelta);
    });
  }
  return null;
};

export const getUrlParameter = name => {
  const urlName = name.replace(/\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp(`[\\?&]${urlName}=([^&#]*)`);
  const results = regex.exec(window.location.search) || regex.exec(window.location.href);
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const nonFoxNowSkus = [
  'us.fs1',
  'us.fs2',
  'us.foxdep',
  'us.btn-btn2go',
  'us.foxSports',
  'us.FoxNews',
  'us.FoxBusiness',
];

export const filterMissingDeepLinkItems = (items, deepLinkSkus) =>
  items.filter(item => {
    const deepLinkWeb = item?.deeplinkWeb;
    if (deepLinkWeb?.length > 0) return true;
    const filteredSkus =
      item?.contentSKUResolved?.reduce((acc, sku) => {
        if (deepLinkSkus?.includes(sku?.name)) {
          acc.push(sku);
        }
        return acc;
      }, []) ?? [];
    return filteredSkus.length === 0;
  });

export const isStaticRender = () => typeof documentFake === 'boolean';

export const getAuthZEntitlementIssues = response => {
  if (!response) return null;
  const responseStatus = response?.status;
  const errorStatus = response?.error?.errorCode;
  let authZEntitlementIssues;
  if (responseStatus) authZEntitlementIssues = _.get(response, 'data.entitlementIssues', []);
  else if (errorStatus) authZEntitlementIssues = _.get(response, 'error.entitlementIssues', []);

  const isAuthZ = authZEntitlementIssues.length > 0;
  if (!isAuthZ) return null;

  const entitlementIssues = _.map(authZEntitlementIssues, i => ({
    ...i,
    statusCode: responseStatus || errorStatus,
  }));
  return entitlementIssues;
};

export const determineErrorFromResponse = action => {
  if (isStaticRender()) return null;

  const response = _.get(action, 'payload.response');
  const responseStatus = _.get(response, 'status');

  const authZEntitlementIssues = getAuthZEntitlementIssues(response);
  if (authZEntitlementIssues) return authZEntitlementIssues;

  const is400Error = responseStatus >= 400 && responseStatus <= 499;
  if (is400Error) return 404;

  const is500Error = responseStatus >= 500 && responseStatus <= 599;
  if (is500Error) return 500;

  return null;
};

export const determineErrorIfStaticRender = () => {
  if (isStaticRender()) return null;
  return 404;
};

const getError500Component = () => {
  const breakpoint = getSitePlatform();
  const branding = getNetworkBranding();

  const props = {
    className: 'ft-error',
    breakpoint,
    branding,
  };
  return <Error500 data-test="error-500" {...props} />;
};

const getError404Component = () => {
  const breakpoint = getSitePlatform();
  const branding = getNetworkBranding();

  const props = {
    className: 'ft-error',
    breakpoint,
    branding,
  };
  return <Error404 data-test="error-404" {...props} />;
};

export const getErrorComponent = errorCode => {
  if (errorCode === 500) return getError500Component();
  if (errorCode === 404) return getError404Component();
  return null;
};

export const forceHttps = url => _.replace(url, 'http://', '//');

export const getSocialLinksFromContent = content => {
  const socialIconPath = icon => `https://static-media.fox.com/foxnow/web/v2-0/img/${icon}.png`;
  const socialIconPathLocal = icon => `${helperGetStaticContentUrl('images', `${icon}.png`)}`;
  const socialLinkStructure = [
    {
      name: 'Facebook',
      key: 'facebookId',
      icon: 'fb-logo',
      format: id => `https://www.facebook.com/${id}/`,
    },
    {
      name: 'Twitter',
      key: 'twitterId',
      icon: 'twitter',
      format: id => `https://twitter.com/${id}`,
    },
    {
      name: 'Instagram',
      key: 'instagramId',
      localIcon: 'ig-logo',
      format: id => `https://www.instagram.com/${id}/`,
    },
    {
      name: 'Youtube',
      key: 'youtubeId',
      icon: 'youtube',
      format: id => `https://www.youtube.com/c/${id}/`,
    },
  ];
  return _.map(socialLinkStructure, structure => {
    const id = _.get(content, structure.key);

    if (!id) return null;
    const { key, name } = structure;
    const url = structure.format(id);
    let icon = socialIconPath(structure.icon);
    if (!structure.icon && structure.localIcon) icon = socialIconPathLocal(structure.localIcon);
    return { key, name, url, icon };
  });
};

export const urlIsExternal = url => {
  if (_.startsWith(url, 'http')) return true;
  if (_.startsWith(url, 'www')) return true;
  return false;
};

export const formatDirectUrl = url => {
  if (_.startsWith(url, 'www')) return `http://${url}`;
  return url;
};

export const getUrlTarget = url => {
  if (urlIsExternal(url)) {
    return {
      target: '_blank',
      rel: LINK_REL,
    };
  }
  return {};
};

export const truncateText = (text, charCount = 30, append = '...') => {
  if (!text) return null;
  if (text.length < charCount) return text;
  const truncatedText = text.substr(0, charCount + append.length);
  const truncatedTextWithoutBrokenWords = truncatedText
    .split(' ')
    .slice(0, -1)
    .join(' ');
  const result = `${truncatedTextWithoutBrokenWords}${append}`;
  return result;
};

export const truncateDesc = (description, enableFeature) => {
  if (isDescTooLong(description) && enableFeature) {
    return `${description.substring(0, SPORTS_MAX_DESC_LENGTH)}...`;
  }
  return description;
};

export const isArticleTab = tab => {
  const { url } = tab;
  return url && url.indexOf('article/') !== -1;
};

export const isSportsVideoLive = video => {
  const status = _.get(video, 'contentLabel[0]', '');
  return status.toLowerCase() === 'live';
};

export const isSportsContent = video => {
  const seriesType = _.get(video, 'seriesType');
  const isSportsType = seriesType === TYPE_SPORTING_EVENT;
  const hasSportsFeatures = sportsFeatures();
  return !!(isSportsType && hasSportsFeatures);
};

export const isSportsCollection = videos => {
  if (!videos || !videos.length) return false;
  let isSports = true;
  // only return true if ALL videos are sports content
  _.forEach(videos, v => {
    if (!isSportsContent(v)) isSports = false;
  });
  return isSports;
};

export const concatToUrl = (url, stringToConcat) => {
  if (!url || !stringToConcat) return url;
  // remove empty question mark at the end.
  let newUrl = url.replace(/\?$/, '');
  const hasQuery = newUrl.indexOf('?') === -1;
  const queryGlue = hasQuery ? '?' : '&';
  // replace && to &
  newUrl = `${newUrl}${queryGlue}${stringToConcat}`.replace(/&&/, '&');
  return newUrl;
};

export const checkContentLabel = ({ video, label, labels }) => {
  const contentLabel = _.get(video, 'contentLabel', null);
  if (!contentLabel) return false;
  const contentLabels = _.map(contentLabel, l => l.toLowerCase());
  let hasLabel = false;
  if (label && _.includes(contentLabels, label.toLowerCase())) hasLabel = true;
  if (labels) {
    _.forEach(labels, l => {
      if (_.includes(contentLabels, l.toLowerCase())) hasLabel = true;
    });
  }
  return hasLabel;
};

export const videoIsLive = video => {
  const livePlayerScreenUrl = _.get(video, 'livePlayerScreenUrl', null);
  return !!livePlayerScreenUrl;
};

export const isInternalLink = (link = '', base) => {
  const baseLink = base || BaseUrl;
  if (!link || !baseLink) return false;

  const http = 'http://';
  const https = 'https://';
  const startsWith = _.startsWith(link.replace(https, http), baseLink.replace(https, http));
  return !!startsWith;
};

export const updateNextPanels = ({ panels, upNextVideos }) => {
  const newPanels = _.cloneDeep(panels);
  const member = _.get(newPanels, 'member', []);
  const currentVideo = upNextVideos[0];
  const currentVideoId = _.get(currentVideo, 'id', null);
  if (!member || !member.length || !currentVideoId) return null;

  const currentVideoEndDate = _.get(currentVideo, 'endDate', null);

  const currentVideoStillValid =
    currentVideoEndDate && isAfter(stringToDate(currentVideoEndDate), now());

  const nextVideo = currentVideoStillValid ? upNextVideos[0] : upNextVideos[1] || null;
  const nextVideoStartDate = _.get(nextVideo, 'startDate', null);
  const nextVideoStarted = nextVideoStartDate
    ? !isAfter(stringToDate(nextVideoStartDate), now())
    : false;
  newPanels.member = _.map(member, panel => {
    const newPanel = _.cloneDeep(panel);
    const items = _.get(newPanel, 'items.member', []);
    const itemIdx = _.findIndex(items, i => currentVideoId === _.get(i, 'id'));
    if (itemIdx === -1) return newPanel;
    if (nextVideoStarted) {
      newPanel.items.member[itemIdx] = nextVideo;
    } else {
      newPanel.items.member.splice(itemIdx, 1);
    }
    return newPanel;
  });
  return newPanels;
};

const getUpNextFetchData = ({ video, isUpNext }) => {
  const { id: videoId, startDate, endDate } = video;
  const fetchDate = isUpNext ? startDate : endDate;
  if (!videoId || !fetchDate) return null;
  const fetchInMs = differenceInMilliseconds(stringToDate(fetchDate), now());
  return { videoId, fetchInMs };
};

const setFetchUpNextTimeout = ({ fetchData, fetchCb, setTimeoutCb }) => {
  if (!fetchData) return null;
  const { accessToken, deviceId, videoId, fetchInMs } = fetchData;
  let newTimeout = null;
  if (fetchInMs < 0) {
    fetchCb({ videoId, accessToken, deviceId });
  } else {
    newTimeout = setTimeoutCb(() => {
      fetchCb({ videoId });
    }, fetchInMs);
  }
  return newTimeout;
};

export const setLocalChannelCookies = ({
  videoId = '',
  playerScreenUrl = '',
  headline = '',
  playlist = '',
  resetCookies = false,
}) => {
  const path = { path: '/' };
  if (resetCookies) {
    removeLocalClipPage({ options: path });
    removeLocalClipPlaylist({ options: path });
    removeCookie({ key: LOCAL_CLIP_INITIAL_ID, options: path });
    removeCookie({ key: LOCAL_CLIP_INITIAL_PLAYER_URL, options: path });
    removeLocalClipSectionTitle({ options: path });
    removeOomLivePlayerEnabled({ key: OOM_LIVE_PLAYER_URL_COOKIE_NAME, options: path });
    return;
  }

  setCookie({
    name: LOCAL_CLIP_INITIAL_ID,
    value: videoId,
    options: path,
  });
  setCookie({
    name: LOCAL_CLIP_INITIAL_PLAYER_URL,
    value: playerScreenUrl,
    options: path,
  });
  setLocalClipPage({
    value: playerScreenUrl,
    options: path,
  });
  setLocalClipSectionTitle({
    value: headline,
    options: path,
  });
  setLocalClipPlaylist({
    value: playlist,
    options: path,
  });
};

export const setOutOfMarketLocalCookies = ({ resetCookies = false, ...item }) => {
  const path = { path: '/' };

  if (resetCookies) {
    removeOomLivePlayerEnabled({ key: OOM_LIVE_PLAYER_URL_COOKIE_NAME, options: path });
    removeCookie({ key: OOM_LIVE_PLAYER_IMAGE_COOKIE_NAME, options: path });
    return;
  }

  setOomLivePlayerEnabled({
    name: OOM_LIVE_PLAYER_URL_COOKIE_NAME,
    value: {
      contentRating: item.contentRating,
      description: item.description,
      headline: item.headline,
      images: item.images,
      name: item.name,
      originalAirDate: item.originalAirDate,
    },
    options: path,
  });
  setCookie({
    name: OOM_LIVE_PLAYER_IMAGE_COOKIE_NAME,
    value: item.image,
    options: path,
  });
};

const fetchedLive = [];
const fetchedUpNext = [];
export const getUpNextTimeouts = ({ accessToken, deviceId, panels, fetchCb, setTimeoutCb }) => {
  const newTimeouts = [];
  _.forEach(panels, panel => {
    const items = _.get(panel, 'items.member');
    if (!items || !items.length) return;
    _.forEach(items, item => {
      const isLive = videoIsLive(item);
      const isUpNext = checkContentLabel({ video: item, label: 'up next' });
      const id = _.get(item, 'id');
      const didFetchLive = _.includes(fetchedLive, id);
      const didFetchUpNext = _.includes(fetchedUpNext, id);
      const excluded = (isLive && didFetchLive) || (isUpNext && didFetchUpNext);

      if (!excluded && item && (isLive || isUpNext)) {
        if (isLive) {
          fetchedLive.push(id);
        } else if (isUpNext) {
          fetchedUpNext.push(id);
        }

        const upNextFetchData = getUpNextFetchData({ video: item, isUpNext });
        const fetchData = { ...upNextFetchData, accessToken, deviceId };
        const newTimeout = setFetchUpNextTimeout({ fetchData, fetchCb, setTimeoutCb });
        if (newTimeout) newTimeouts.push(newTimeout);
      }
    });
  });
  return newTimeouts;
};

export const getSeasonEpisodeCounts = ({ currentSeason, seasonCount, episodeCount, useComma }) => {
  if (!seasonCount && !episodeCount) return null;
  let seasonString = '';
  let episodeString = '';
  if (seasonCount) {
    const seasonLabel = seasonCount > 1 ? 'Seasons' : 'Season';
    if (seasonCount > 1 || !currentSeason) {
      seasonString = `${seasonCount} ${seasonLabel}`;
    } else {
      seasonString = `${seasonLabel} ${currentSeason.seasonNumber}`;
    }
  }
  if (episodeCount) {
    const episodeLabel = episodeCount > 1 ? 'Episodes' : 'Episode';
    episodeString = `${episodeCount} ${episodeLabel}`;
  }
  const hideSeparator = !seasonCount || !episodeCount;
  let separator = '';
  if (!hideSeparator) separator = useComma ? ', ' : ' ';
  return `${seasonString}${separator}${episodeString}`;
};

export const getShowType = ({ videoType = '', seriesType }) => {
  if (isSeries(seriesType) || isSpecial(seriesType) || isNews(seriesType)) return 'Show';
  if (isMovie(videoType) || isMovie(seriesType)) return 'Movie';
  return '';
};

export const getLiveMetaData = ({ startDate, durationInSeconds }) => {
  const startTime = startDate ? format(stringToDate(startDate), FORMAT_TIME) : '';
  const formattedDuration = formatDurationInSeconds(durationInSeconds);
  return formatMetadata([startTime, formattedDuration]);
};

export const getMovieMetadata = ({ releaseYear = '', durationInSeconds }) => {
  const formattedDuration = formatDurationInSeconds(durationInSeconds);
  return formatMetadata([releaseYear, formattedDuration]);
};

export const getOriginalAirdateMetaData = ({ originalAirDate, durationInSeconds }) => {
  const airDate = originalAirDate
    ? format(stringToDate(originalAirDate), FORMAT_MONTH_DAY_YEAR)
    : '';
  const formattedDuration = formatDurationInSeconds(durationInSeconds);
  return formatMetadata([airDate, formattedDuration]);
};

export const castProcessor = actor => {
  const name = _.get(actor, 'name', null);
  return name;
};

export const getVideoIdFromUrl = url => {
  if (!url) return null;
  const urlParts = url.split('/');
  const videoId = urlParts[2];
  return videoId;
};

export const getEPGListingIdFromPlayerScreenUrl = url => {
  const splitUrl = url.split(/=|&/);
  const epgListingIndex = splitUrl.indexOf('epgListingId') + 1;
  const epgListingId = epgListingIndex ? splitUrl[epgListingIndex] : null;
  return epgListingId;
};

export const getRequiresAuth = ({ video, isLive = false }) => {
  let requiresAuthVod = _.get(video, 'requiresAuth', false);
  let requiresAuthLive = _.get(video, 'requiresAuthLive', false);

  const requiredEntitlements = _.get(video, 'requiredEntitlements', null);
  if (requiredEntitlements) {
    const vodRequirements = _.get(requiredEntitlements, 'vod', []);
    const liveRequirements = _.get(requiredEntitlements, 'live', []);
    requiresAuthVod = vodRequirements.length > 0 || requiresAuthVod;
    requiresAuthLive = liveRequirements.length > 0 || requiresAuthLive;
  }

  return isLive ? requiresAuthLive : requiresAuthVod;
};

export const getAuthForPanelVideo = (item, isLive, accessToken) => {
  let mvpdid = '';
  if (accessToken) mvpdid = parseJwt(accessToken)?.mvpdid;

  return (
    getRequiresAuth({ video: item, isLive }) ||
    mvpdid === PREVIEW_PASS_60_MVPD ||
    mvpdid === PREVIEW_PASS_5_MVPD
  );
};

export const getAuthorizingNetwork = (item, isLive, accessToken) => {
  const requiresAuth = getAuthForPanelVideo(item, isLive, accessToken);
  if (!requiresAuth) return NO_AUTHORIZING_NETWORK;

  const network = _.get(item, 'network');
  return network || NO_AUTHORIZING_NETWORK;
};

export const setLongTimeout = function setLongTimeout(...params) {
  const [callback, milliseconds] = params;
  if (typeof callback !== 'function') {
    throw new Error('Callback must be a function');
  }
  const ms = parseInt(milliseconds, 10);
  if (Number.isNaN(ms)) {
    throw new Error('Delay must be an integer');
  }

  const args = Array.prototype.slice.call(params, 2);
  const cb = callback.bind(...[this].concat(args));

  const longTimeout = {
    timer: null,
    clear: function clear() {
      if (this.timer) {
        clearTimeout(this.timer);
      }
    },
  };

  const max = 2147483647;
  if (ms <= max) {
    longTimeout.timer = setTimeout(cb, ms);
  } else {
    let count = Math.floor(ms / max); // the number of times we need to delay by max
    const rem = ms % max; // the length of the final delay
    (function delay() {
      if (count > 0) {
        count -= 1;
        longTimeout.timer = setTimeout(delay, max);
      } else {
        longTimeout.timer = setTimeout(cb, rem);
      }
    })();
  }
  return longTimeout;
};

export const clearLongTimeout = longTimeoutObject => {
  const isFunction = longTimeoutObject && typeof longTimeoutObject.clear === 'function';
  if (isFunction) {
    longTimeoutObject.clear();
  }
};

export const plurarize = ({ count, singular, plural, separator = ' ', reverse = false }) => {
  const label = count === 1 ? singular : plural;
  const text = reverse ? `${label}${separator}${count}` : `${count}${separator}${label}`;
  return text;
};

export const slugify = text => {
  const slug = text || '';
  const a = 'àáäâèéëêìíïîòóöôùúüûñçßÿœæŕśńṕẃǵǹḿǘẍźḧ·/_,:;άαβγδεέζήηθιίϊΐκλμνξοόπρσςτυϋύΰφχψωώ';
  const b = 'aaaaeeeeiiiioooouuuuncsyoarsnpwgnmuxzh------aavgdeeziitiiiiklmnxooprsstyyyyfhpoo';
  const p = new RegExp(a.split('').join('|'), 'g');

  return slug
    .toString()
    .trim()
    .toLowerCase()
    .replace(/ου/g, 'ou')
    .replace(/ευ/g, 'eu')
    .replace(/θ/g, 'th')
    .replace(/ψ/g, 'ps')
    .replace(/\//g, '-')
    .replace(Regex.SPACES, '-') // Replace spaces with -
    .replace(p, c => b.charAt(a.indexOf(c))) // Replace special chars
    .replace(/&/g, '-and-') // Replace & with 'and'
    .replace(Regex.NON_WORD_CHARACTERS, '') // Remove all non-word chars
    .replace(Regex.MULTIPLE_DASHES, '-') // Replace multiple - with single -
    .replace(Regex.LEADING_DASH, '') // Trim - from start of text
    .replace(Regex.TRAILING_DASH, ''); // Trim - from end of text
};

export const formatUpcomingDate = dateString => {
  const startingDate = stringToDate(dateString);
  if (!startingDate) return null;
  const { epg_todayHeader: labelToday, epg_tomorrowHeader: labelTomorrow } = window.Language;
  const today = now();
  const tomorrow = addDays(today, 1);
  const videoStartsToday = isSameDay(startingDate, today);
  const videoStartsTomorrow = isSameDay(startingDate, tomorrow);
  const videoStartsNextWeek = differenceInDays(startingDate, today) >= 7;

  let dateLabel;
  if (videoStartsToday) {
    dateLabel = labelToday;
  } else if (videoStartsTomorrow) {
    dateLabel = labelTomorrow;
  } else if (videoStartsNextWeek) {
    dateLabel = format(startingDate, FORMAT_MONTH_SLASH_DAY);
  } else {
    dateLabel = format(startingDate, FORMAT_DAY_OF_WEEK);
  }
  const formattedTime = format(startingDate, FORMAT_TIME);

  return `${dateLabel}, ${formattedTime}`;
};

export const isUpcomingContent = item => {
  if (!item) return false;
  const { '@type': type, startDate } = item;
  const isLiveItem = isLive(type);
  const isInFuture = isAfter(startDate, now());
  const isUpcoming = isLiveItem && isInFuture;
  return isUpcoming;
};

export const isLiveVideoExpired = endDate => isAfter(now(), endDate);

export const isCurrentLiveContent = item => {
  if (!item) return false;
  const { '@type': type, endDate } = item;
  const itemIsLive = isLive(type);
  const itemIsExpired = isLiveVideoExpired(endDate);
  const itemIsUpcoming = isUpcomingContent(item);
  if (itemIsLive && !itemIsExpired && !itemIsUpcoming) return true;
  return false;
};

const getSecUntilTimestamp = time => differenceInSeconds(msToDate(time), now());

export const calculateNextRefreshForPanel = panel => {
  if (isStaticRender()) return false;
  const items = _.get(panel, 'items.member', []);
  const liveContent = _.filter(items, item => isCurrentLiveContent(item));
  const upcomingContent = _.filter(items, item => isUpcomingContent(item));
  if (!liveContent.length && !upcomingContent.length) return false;

  const dateStringToTimestamp = str => getTimestamp(stringToDate(str));
  const endTimestamps = liveContent.map(item => dateStringToTimestamp(item.endDate));
  const startTimestamps = upcomingContent.map(item => dateStringToTimestamp(item.startDate));
  const STARTING_SOON_TRESHOLD_MS = 5 * 60 * 1000;

  const startingSoonTimestamps = upcomingContent
    .map(item => dateStringToTimestamp(item.startDate) - STARTING_SOON_TRESHOLD_MS)
    .filter(timestamp => isAfter(msToDate(timestamp), now()));

  const timestamps = [...endTimestamps, ...startTimestamps, ...startingSoonTimestamps];
  let nextTimestamp = Math.min(...timestamps);
  let secUntilNextTimestamp = getSecUntilTimestamp(nextTimestamp);
  if (secUntilNextTimestamp === 0) {
    _.remove(timestamps, t => t === nextTimestamp);
    nextTimestamp = Math.min(...timestamps);
    secUntilNextTimestamp = getSecUntilTimestamp(nextTimestamp);
  }
  return secUntilNextTimestamp + 1; // add 1 to prevent refetching data too soon
};

export const getPreviewVideoCTAText = panel => {
  if (!panel) return null;
  const {
    previewVideoCTA_movie: movie,
    previewVideoCTA_personality: personality,
    previewVideoCTA_series: series,
    previewVideoCTA_seriesCollection: seriesCollection,
    previewVideoCTA_special: special,
  } = window.Language;
  const type = _.get(panel, '@type');
  const isSeriesPanel = isTypeSeries(type);
  const isCategoryPanel = isTypeCategory(type);
  if (isSeriesPanel) {
    const seriesType = _.get(panel, 'seriesType');

    if (isMovie(seriesType)) return movie;
    if (isSeries(seriesType)) return series;
    if (isSeriesCollection(seriesType)) return seriesCollection;
    if (isSpecial(seriesType)) return special;
  } else if (isCategoryPanel) {
    const categoryType = _.get(panel, 'categoryType');

    if (isCategoryTypePersonality(categoryType)) return personality;
  }

  return null;
};

export const shouldBlockEndcardsForTrailer = (video, preauthorizedResources) => {
  if (!video) return null;
  const { clipType, videoType, isTrailer } = video;
  const videoIsTrailer = (isClip(videoType) && isClipTrailer(clipType)) || isTrailer;
  if (videoIsTrailer) return true;
  return false;
};

export const getStickBannerData = state => {
  const members = _.get(state, 'collection.panels.member', []);
  const promoPanels = members.filter(member => member && member.panelType === PANEL_TYPE_PROMOS);
  const stickyBannerPanel = promoPanels.find(panel => {
    const promoType = _.get(panel, 'items.member.0.promoType', null);
    return promoType === 'stickyBanner';
  });
  const stickyBannerData = _.get(stickyBannerPanel, 'items.member.0', null);
  return stickyBannerData;
};

// Determines if the user is using an ad blocker extension by attempting to fetch
// Google's ad file, any ad blocker should block this request.
export async function checkAdBlocker() {
  let isBlocked;
  async function tryRequest() {
    try {
      return axios
        .get('https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js', {
          method: 'HEAD',
          mode: 'no-cors',
        })
        .then(() => {
          isBlocked = false;
          return isBlocked;
        })
        .catch(() => {
          isBlocked = true;
          return isBlocked;
        });
    } catch (error) {
      console.log(error);
      isBlocked = true;
      return isBlocked;
    }
  }

  if (isBlocked !== undefined) {
    return isBlocked;
  }

  try {
    return await tryRequest();
  } catch (error) {
    console.log(error);
  }

  return false;
}

export const getCarouselBreakpoint = ({ type }) => {
  switch (type) {
    case 'poster':
      return [
        {
          breakpoint: 767,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 1023,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 1439,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4,
          },
        },
        {
          breakpoint: 2559,
          settings: {
            slidesToShow: 5,
            slidesToScroll: 5,
          },
        },
        {
          breakpoint: 2560,
          settings: {
            slidesToShow: 6,
            slidesToScroll: 6,
          },
        },
      ];
    case MORE_LIKE_POSTER:
      return [
        {
          breakpoint: 767,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 1023,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4,
          },
        },
        {
          breakpoint: 1439,
          settings: {
            slidesToShow: 5,
            slidesToScroll: 5,
          },
        },
        {
          breakpoint: 2560,
          settings: {
            slidesToShow: 6,
            slidesToScroll: 6,
          },
        },
      ];
    case SIMPLIFIED_POSTER:
      return [
        {
          breakpoint: 767,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 1023,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4,
          },
        },
        {
          breakpoint: 1439,
          settings: {
            slidesToShow: 5,
            slidesToScroll: 5,
          },
        },
        {
          breakpoint: 2560,
          settings: {
            slidesToShow: 6,
            slidesToScroll: 6,
          },
        },
      ];
    case SERIES_CLIP_TILE:
    case SERIES_SEASON_TILE:
      return [
        {
          breakpoint: 767,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
          },
        },
        {
          breakpoint: 1023,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 2,
          },
        },
        {
          breakpoint: 1439,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 3,
          },
        },
        {
          breakpoint: 2559,
          settings: {
            slidesToShow: 4,
            slidesToScroll: 4,
          },
        },
        {
          breakpoint: 2561,
          settings: {
            slidesToShow: 5,
            slidesToScroll: 5,
          },
        },
      ];
    default:
      return [];
  }
};
